import { components } from "@/api-schema";
import { PathsV1BranchSearchPostParametersQueryExtend } from "@/api-schema-enums";
import { useBranchSearchQuery } from "@/common/api/branch/use-branch-search.query";
import { useComboboxPagination } from "@/common/hooks/use-combobox-pagination";
import { BaseFilterProps, Filter, FilterData, FilterPaginationState } from "../_types";
import { FilterComponent } from "../base-filter";

export type QueryBody = components["schemas"]["BranchesSearchBody"];

const FILTER_PAGE_SIZE = 50;

export const BRANCH_NOT_SET = "IS_NULL";

export class BranchFilter extends Filter<QueryBody, unknown> {
  readonly id: string = "branches";
  readonly labelSingular = "common:Branch";
  readonly labelPlural = "common:Branches";
  readonly placeholder = "ram:SelectBranches";
  readonly searchPlaceholder = "common:SearchBranches";
  readonly emptyText = "common:NoBranchesFound";
  readonly includeWithoutBranch: boolean;

  constructor({
    id,
    body = {},
    includeWithoutBranch = false,
  }: { id?: string; body?: Partial<QueryBody>; includeWithoutBranch?: boolean } = {}) {
    super();
    if (id) this.id = id;
    this.body = body as QueryBody;
    this.includeWithoutBranch = includeWithoutBranch;
  }

  getData(): FilterData {
    return { options: [], isLoading: false, error: null };
  }

  render(props: BaseFilterProps) {
    return <BranchFilterComponent filter={this} {...props} />;
  }
}

function BranchFilterComponent({ filter, ...props }: { filter: BranchFilter } & BaseFilterProps) {
  const { data, pagination } = useBranchFilterData(filter.body, filter.includeWithoutBranch);

  const filterWithData = Object.create(filter);
  filterWithData.getData = () => data;

  return <FilterComponent filter={filterWithData} pagination={pagination} {...props} />;
}

const branchNameCollator = new Intl.Collator("de", { sensitivity: "base", numeric: true });

function useBranchFilterData(
  body: QueryBody,
  includeWithoutBranch: boolean,
): { data: FilterData; pagination: FilterPaginationState } {
  const { debouncedSearchTerm, pageSize, createPaginationState } = useComboboxPagination({
    initialPageSize: FILTER_PAGE_SIZE,
  });

  const {
    data: branches,
    isPending: isLoading,
    error,
  } = useBranchSearchQuery({
    options: {
      parameters: {
        pageSize,
        search: debouncedSearchTerm || undefined,
        extend: [
          PathsV1BranchSearchPostParametersQueryExtend.Company,
          PathsV1BranchSearchPostParametersQueryExtend.Brands,
          PathsV1BranchSearchPostParametersQueryExtend.Address,
          PathsV1BranchSearchPostParametersQueryExtend.BranchIdentifiers,
        ],
      },
      body,
    },
  });

  const options = buildBranchFilterOptions(branches?.data, { includeWithoutBranch });

  const loadedCount = branches?.data?.length ?? 0;
  const total = branches?.meta?.total ?? 0;

  const pagination = createPaginationState(total, loadedCount, isLoading);

  return {
    data: {
      options,
      isLoading,
      error,
    },
    pagination,
  };
}

export function getBranchFilterOptionName(branch: components["schemas"]["Branch"]): string {
  return branch.name.trim();
}

export function buildBranchFilterOptions(
  branches?: components["schemas"]["Branch"][],
  { includeWithoutBranch = false }: { includeWithoutBranch?: boolean } = {},
): FilterData["options"] {
  const withoutBranchOptions = includeWithoutBranch ? [{ id: BRANCH_NOT_SET, name: "common:WithoutBranch" }] : [];

  if (!branches?.length) {
    return withoutBranchOptions;
  }

  const options: FilterData["options"] = [];
  const seenIds = new Set<string>();

  for (const branch of branches) {
    const id = branch.uuid?.trim();

    if (!id || seenIds.has(id)) {
      continue;
    }

    seenIds.add(id);
    options.push({ id, name: getBranchFilterOptionName(branch) });
  }

  options.sort((left, right) => branchNameCollator.compare(left.name, right.name));

  return [...withoutBranchOptions, ...options];
}
